import sortBy from 'lodash/sortBy'
import React, { useEffect, useMemo, useState } from 'react'
import { connect, useSelector } from 'react-redux'

/* Components */

import IframeShare from 'components/Artefacts/IframeShare'
import IframeWhiteboard from 'components/Artefacts/IframeWhiteboard'
import ConnectionStatus from 'components/Artefacts/IframeWhiteboard/ConnectionStatus'
import ScreenShare from 'components/Artefacts/ScreenShare'
import Whiteboard from 'components/Artefacts/Whiteboard/Whiteboard'
import AudioTracks from 'components/AudioTrack'
import { ImageCarouselView } from 'components/common/ImageCarouselView'
import Loader from 'components/common/Loader'
import { TopControlBar, BottomControlBar } from 'components/ControlBar2.0'
import Layout from 'components/Layouts'
import ClassRoomMobile from 'components/Layouts/ClassRoomMobile'
import { LoginQRCode } from 'components/LoginQRCode'
import SettingsDialog from 'components/MenuBar/SettingsDialog/SettingsDialog'
import { MyDrive } from 'components/MyDrive'
import { Notes } from 'components/Notes'
import Participant from 'components/Participant/Participant'
import Quiz from 'components/Quiz/Revamp'
import { GiphySearch } from 'components/Tiles/components/Giphy'
import { userTypes } from 'constants/user'
import { emitShowGiphyMsg, emitTriggerGiphyModal } from 'helpers/events'
import useGhostUser from 'hooks/useGhostUser/useGhostUser'
import useRoomStateHandler from 'hooks/useRoomStateHandler'
import useScreenShareParticipant from 'hooks/useScreenShareParticipant/useScreenShareParticipant'
import useUserActivityTracker from 'hooks/useUserActivityTracker/useUserActivityTracker'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import {
    QUIZ,
    SECONDARY_DEVICE,
    WHITEBOARD,
} from 'services/webSockets/roomState/constants'
import { setRoomState } from 'store/actions/Room'
import { sendSocketMessage } from 'store/actions/WebSocket'
import { dispatchSetStudentsCount } from 'store/dispatcher'
import { dispatchUpdateIsTeacherPresent } from 'store/dispatcher/Room'
import { _previousWhiteboard } from 'store/selectors/Artefacts'
import { _participants } from 'store/selectors/Room'
import { _roomStateMsgData } from 'store/selectors/WebSocket'
import { getNotes } from 'store/thunks/Note'
import { isDesktop } from 'utils/device'
import { setTeacherGender } from 'utils/gender'
import { logger } from 'utils/logger'

import FeatureAvailability from './FeatureAvailability'
import { Container, Header, Body, Footer } from './styles'

import useParticipants, {
    getIdentity,
} from '../../hooks/useParticipants/useParticipants'
import { getRoomState } from '../../services/apis/room'

/* Styles */
import './Chat-widget.css'

let Room = props => {
    /* Hooks */
    const {
        room,
        userId,
        userType,
        roomName,
        isGhost,
        isWhiteboardMode,
        setWhiteboardMode,
        isQuizMode,
        setQuizMode,
        toggleTeacherPresent,
        remoteDataPub,
        jeopardy,
    } = useVideoContext()

    const isTeacher = userType === userTypes.TEACHER

    const screenShareParticipant = useScreenShareParticipant()
    const data = useSelector(_roomStateMsgData)

    const remoteParticipants = useSelector(_participants)
    const { teacher, remoteStudents, localParticipant } = useParticipants()
    const { isIframeShared } = jeopardy
    const previousWhiteboard = useSelector(_previousWhiteboard)

    useRoomStateHandler(props.roomStateMsg)

    // For Ghost User
    useGhostUser()
    useUserActivityTracker({ isTeacher })

    /* Local States */
    const [loading, setLoading] = useState(true)

    const isSecondaryDevice = data === SECONDARY_DEVICE && isTeacher

    useEffect(() => {
        // fetch notes for late comers
        props.getNotes(roomName)
    }, [])

    useEffect(() => {
        /**
         * call room disconnect on room component unmount
         * this will handle every case
         */

        return () => {
            room.disconnect()
        }
    }, []) // dont pass room dependency here

    useEffect(() => {
        // eslint-disable-next-line no-extra-semi
        ;(async () => {
            try {
                let response = await getRoomState(roomName)
                const {
                    error,
                    teacher_gender,
                    state,
                    protocol,
                    teacher_present,
                } = response.data
                if (error) return

                props.setRoomState({ ...response.data, userId })

                setTeacherGender(teacher_gender)

                if (state !== QUIZ && state !== WHITEBOARD) return

                setWhiteboardMode(false)
                setQuizMode(false)
                if (state === WHITEBOARD) {
                    setWhiteboardMode(true)
                } else if (state === QUIZ) {
                    setQuizMode(true)
                }

                toggleTeacherPresent(teacher_present || isTeacher)
                dispatchUpdateIsTeacherPresent(teacher_present || isTeacher)
            } catch (error) {
                logger.error({ msg: `Get room state api call failed`, error })
            }
        })()
    }, [])

    /* Sets loading to false */
    useEffect(() => {
        if (!loading) return
        setTimeout(() => {
            setLoading(false)
        }, 4000)
    }, [loading])

    const onGifClick = data => {
        emitShowGiphyMsg({ giphyId: data.id })
        emitTriggerGiphyModal(false)
    }

    const teacherParticipant = useMemo(() => {
        if (!teacher) return null

        const isLocal = teacher === localParticipant

        const props = {
            isLocal,
            participant: teacher,
            identity: getIdentity(teacher.identity),
        }

        return <Participant {...props} />
    }, [teacher, localParticipant])

    const studentParticipants = useMemo(() => {
        const identity = getIdentity(localParticipant.identity)
        const allStudents = [...remoteStudents]
        const allStudentsSortedArray = sortBy(allStudents, function(item) {
            return remoteParticipants.findIndex(remoteParticipant =>
                item.identity.startsWith(remoteParticipant.id)
            )
        })
        if (!identity.isTeacher && !identity.isGhost) {
            allStudentsSortedArray.unshift(localParticipant)
        }

        dispatchSetStudentsCount(allStudentsSortedArray.length)

        return allStudentsSortedArray.map(student => {
            const identity = getIdentity(student.identity)
            const props = {
                identity,
                participant: student,
                isLocal: student === localParticipant,
            }

            return <Participant key={`student-${identity.uuid}`} {...props} />
        })
    }, [remoteStudents, remoteParticipants, localParticipant])

    useEffect(() => {
        localStorage.setItem('sessionId', localParticipant.sid)
    }, [localParticipant])

    const render = () => {
        if (loading) {
            return <Loader overlay bgColor="#222" />
        }

        // removing it to handle potrait mode on new view
        // TODO: remove the unused component LandscapeMode
        // if (props.isPortrait && isMobileOrIPad) return <LandscapeMode />

        const layoutProps = {
            students: studentParticipants,
            teacher: teacherParticipant,
            artefact: null,
            mode: 'discussion',
        }

        const isScreenShareMode = screenShareParticipant ? true : false

        layoutProps.artefact = isScreenShareMode ? (
            <ScreenShare participant={screenShareParticipant} />
        ) : (
            layoutProps.artefact
        )

        layoutProps.artefact = isQuizMode ? <Quiz /> : layoutProps.artefact
        layoutProps.artefact = isWhiteboardMode ? (
            previousWhiteboard ? (
                <Whiteboard remoteDataPub={remoteDataPub} />
            ) : !isSecondaryDevice ? (
                <IframeWhiteboard />
            ) : (
                layoutProps.artefact
            )
        ) : (
            layoutProps.artefact
        )

        if (isIframeShared) {
            layoutProps.artefact = <IframeShare isTeacher={isTeacher} />
        }

        let layout = <Layout {...layoutProps} />

        if (!isDesktop)
            return (
                <>
                    <ClassRoomMobile {...layoutProps} />
                    <AudioTracks />
                </>
            )

        return (
            <Container isQuizMode={isQuizMode}>
                <Header>{!isGhost && <TopControlBar />}</Header>
                <Body>{layout}</Body>
                <Footer>{!isGhost && <BottomControlBar />}</Footer>

                <Notes />
                <SettingsDialog />

                {/* ImageCarouselView is being used to display image carousel
                    with zoom, rotate and other functionalities, 
                    used for both filer and group study feature 
                */}
                <ImageCarouselView />
                {isWhiteboardMode && isSecondaryDevice && <ConnectionStatus />}
                <LoginQRCode />
                <GiphySearch onGifClick={onGifClick} />
                <FeatureAvailability isTeacher={isTeacher} />
                <AudioTracks />
                {isTeacher && <MyDrive />}
            </Container>
        )
    }

    console.count('Room Component')

    return render()
}

const mapStateToProps = state => {
    return {
        msg: state.websocket.msg,
        roomStateMsg: state.websocket.roomStateMsg,
        ghostIdentity: state.room.ghostIdentity,
        isSubscribedToGhost: state.room.isSubscribedToGhost,
    }
}

const mapDispatchToProps = dispatch => {
    return {
        setRoomState: payload => dispatch(setRoomState(payload)),
        getNotes: roomName => dispatch(getNotes(roomName)),
        sendSocketMessage: socketMsg => dispatch(sendSocketMessage(socketMsg)),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Room))
